'use client';

import {
  useCallback, useEffect, useState, useMemo, useRef,
} from 'react';

import { CasinoSectionType } from '@/features/v2/home/components/CasinoSection/enums';
import { CasinoGame } from '@/features/v2/home/components/CasinoSection/interface';

import { getCasinoData } from '@/features/v2/shared/services/casinoDataService';

import { useAuthStore } from '@/stores/userStore';

interface UseCasinoDataReturn {
  games: CasinoGame[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCasinoData(
  type: CasinoSectionType,
  instanceId?: string,
  initialGames?: CasinoGame[],
): UseCasinoDataReturn {
  const hasServerBatch = initialGames !== undefined;
  const [games, setGames] = useState<CasinoGame[]>(initialGames ?? []);
  const [isLoading, setIsLoading] = useState(!hasServerBatch);
  const [error, setError] = useState<string | null>(null);

  const favoriteGamesRaw = useAuthStore((state) => { return state.account?.favoriteGames ?? []; });
  const previousFavoriteGamesRef = useRef<string[]>([]);

  const favoriteGames = useMemo(() => {
    const current = favoriteGamesRaw;
    const previous = previousFavoriteGamesRef.current;

    if (current.length !== previous.length) {
      previousFavoriteGamesRef.current = current;
      return current;
    }

    const hasChanged = current.some((id, index) => { return id !== previous[index]; });
    if (hasChanged) {
      previousFavoriteGamesRef.current = current;
      return current;
    }

    return previous;
  }, [favoriteGamesRaw]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const favoriteIds = type === CasinoSectionType.FAVORITES ? favoriteGames : undefined;

      const data = await getCasinoData(type, instanceId, favoriteIds);
      setGames(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Error al cargar ${type}`;

      setError(errorMessage);
      console.warn(`Error fetching ${type} games:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [type, instanceId, favoriteGames]);

  useEffect(() => {
    if (hasServerBatch && type !== CasinoSectionType.FAVORITES) {
      return;
    }

    fetchData();
  }, [fetchData, hasServerBatch, type]);

  return useMemo(() => {
    return {
      games,
      isLoading,
      error,
      refetch: fetchData,
    };
  }, [games, isLoading, error, fetchData]);
}

export function useCasinoGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.CASINO, instanceId);
}

export function useZingaGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.ZINGA, instanceId);
}

export function useFavoritesGames(instanceId?: string) {
  return useCasinoData(CasinoSectionType.FAVORITES, instanceId);
}

export function useGamesDataFactory(type: CasinoSectionType, instanceId?: string) {
  return useCasinoData(type, instanceId);
}
